import * as z from "zod/v4";

const productStatusSchema_ = z.object({
  name: z.string(),
  enabled: z.boolean(),
  enabledAt: z.date().optional(),
});
export const productStatusSchema: z.ZodType<ProductStatus> =
  productStatusSchema_;
export interface ProductStatus extends z.infer<typeof productStatusSchema_> {}

export const conversionEventTypes = [
  "install",
  "returns_enabled",
  "returns_disabled",
  "returns_coverage_enabled",
  "returns_coverage_disabled",
  "fsr_enabled",
  "fsr_disabled",
  "package_protection_coverage_enabled",
  "package_protection_coverage_disabled",
  "extended_warranties_enabled",
  "extended_warranties_disabled",
  "checkout_optimization_enabled",
  "checkout_optimization_disabled",
  "order_tracking_enabled",
  "order_tracking_disabled",
  "support_enabled",
  "support_disabled",
  "pp_plus_percentage_changed",
  "theme_updated",
  "discount_started",
  "discount_ended",
  "checkout_updated",
  "marketing_started",
  "marketing_ended",
] as const;
export type ConversionEventType = (typeof conversionEventTypes)[number];

const conversionEventSchema_ = z.object({
  type: z.enum(conversionEventTypes),
  label: z.string(),
  date: z.date(),
  description: z.string().optional(),
});
export const conversionEventSchema: z.ZodType<ConversionEvent> =
  conversionEventSchema_;
export interface ConversionEvent
  extends z.infer<typeof conversionEventSchema_> {}

const conversionDataPointSchema_ = z.object({
  date: z.string(),
  sessions: z.number(),
  orders: z.number(),
  conversionRate: z.number(),
  totalSales: z.number(),
});
export const conversionDataPointSchema: z.ZodType<ConversionDataPoint> =
  conversionDataPointSchema_;
export interface ConversionDataPoint
  extends z.infer<typeof conversionDataPointSchema_> {}

const conversionMetricsSchema_ = z.object({
  sessions: z.number(),
  orders: z.number(),
  conversionRate: z.number(),
  totalSales: z.number(),
});
export const conversionMetricsSchema: z.ZodType<ConversionMetrics> =
  conversionMetricsSchema_;
export interface ConversionMetrics
  extends z.infer<typeof conversionMetricsSchema_> {}

const dealOwnerSchema_ = z.object({
  ownerId: z.string(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
});
export const dealOwnerSchema: z.ZodType<DealOwner> = dealOwnerSchema_;
export interface DealOwner extends z.infer<typeof dealOwnerSchema_> {}

// cspell:ignore shopperai
// Canonical list lives in redo/server/src/models/ConversionMonitorCache.ts — keep in sync
export const hubSpotRoleNames = [
  "post_sales_owner",
  "returns_product_analyst",
  "oms_product_analyst",
  "marketing_product_analyst",
  "conversion_product_analyst",
  "shopperai_product_analyst",
  "hubspot_owner_id",
] as const;

export type HubSpotRoleName = (typeof hubSpotRoleNames)[number];

const teamPersonSchema_ = z.object({
  role: z.enum(hubSpotRoleNames),
  ownerId: z.string(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
});
export const teamPersonSchema: z.ZodType<TeamPerson> = teamPersonSchema_;
export interface TeamPerson extends z.infer<typeof teamPersonSchema_> {}

const teamSummarySchema_ = z.object({
  teamId: z.string(),
  teamName: z.string(),
  storeUrl: z.string(),
  people: z.array(teamPersonSchema),
  totalMetrics: conversionMetricsSchema.optional(),
  hasRequiredScopes: z.boolean(),
});
export const teamSummarySchema: z.ZodType<TeamSummary> = teamSummarySchema_;
export interface TeamSummary extends z.infer<typeof teamSummarySchema_> {}

const teamConversionDataSchema_ = z.object({
  teamId: z.string(),
  teamName: z.string(),
  storeUrl: z.string(),
  installedAt: z.date().optional(),
  products: z.array(productStatusSchema),
  events: z.array(conversionEventSchema).optional(),
  hasRequiredScopes: z.boolean(),
  missingScopes: z.array(z.string()).optional(),
  preInstallMetrics: conversionMetricsSchema.optional(),
  postInstallMetrics: conversionMetricsSchema.optional(),
  totalMetrics: conversionMetricsSchema,
  dailyData: z.array(conversionDataPointSchema),
  lastUpdated: z.date().optional(),
  isCached: z.boolean().optional(),
});
export const teamConversionDataSchema: z.ZodType<TeamConversionData> =
  teamConversionDataSchema_;
export interface TeamConversionData
  extends z.infer<typeof teamConversionDataSchema_> {}

export const getLongTermConversionMonitor = {
  name: "getLongTermConversionMonitor" as const,
  input: z.object({}),
  output: z.object({
    people: z.array(dealOwnerSchema),
    teamSummaries: z.array(teamSummarySchema),
    totalCount: z.number(),
  }),
};

export type GetLongTermConversionMonitorInput = z.infer<
  typeof getLongTermConversionMonitor.input
>;
export type GetLongTermConversionMonitorOutput = z.infer<
  typeof getLongTermConversionMonitor.output
>;
